import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ClipboardEvent as ReactClipboardEvent,
} from "react";
import { DialView, Icon, type DialVM, type VSection, type VSegment } from "@keydials/dial-workspace";
import { useAuth } from "../auth/AuthContext";
import { KdLogo } from "../components/KdMark";

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;
const EMAIL_RE = /.+@.+\..+/;

type Step = "email" | "otp";
type BannerMessage = { type: "success" | "error"; text: string } | null;

// The five gauge hues (the brand status scale) — used to colour the decorative hero dial + chips.
const SCALE = {
  done: "#3eb034",
  progress: "#f5a51c",
  risk: "#f97316",
  overdue: "#e7161b",
  none: "#94a3b8",
} as const;

// --- Decorative hero dial -------------------------------------------------
// No live dial data exists before sign-in, so the brand panel renders the real product visualization
// (DialView) over a small hand-built sample view model — purely to show off the signature dial. It
// loops red -> amber -> green: each segment drifts toward green at its own random pace (staggered, not
// in order), holds once all are green, then reverts to red and repeats. Because the shared DialView
// only CSS-transitions opacity (not fill), the fade is driven here in JS — interpolating each segment's
// colour every frame and handing DialView a fresh view model. Nothing here touches the API.
const RAG_STOPS = ["#e7161b", "#f5a51c", "#3eb034"] as const; // red -> amber -> green

/** Per-channel RGB lerp between two #rrggbb hexes, returned as an rgb() string. */
function lerpHex(a: string, b: string, t: number): string {
  const ai = parseInt(a.slice(1), 16);
  const bi = parseInt(b.slice(1), 16);
  const ch = (shift: number) => {
    const from = (ai >> shift) & 255;
    const to = (bi >> shift) & 255;
    return Math.round(from + (to - from) * t);
  };
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`;
}

/** Colour for progress p in [0,1]: red (0) -> amber (0.5) -> green (1). */
function ragColour(p: number): string {
  const [red, amber, green] = RAG_STOPS;
  return p < 0.5 ? lerpHex(red, amber, p / 0.5) : lerpHex(amber, green, (p - 0.5) / 0.5);
}

const mean = (xs: number[]): number => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/** The hero dial's fixed structure (section labels + segment counts); only the colours animate. */
const HERO_SECTION_DEFS: { key: string; label: string; count: number }[] = [
  { key: "gov", label: "Governance", count: 3 },
  { key: "fin", label: "Finance", count: 4 },
  { key: "safe", label: "Safeguarding", count: 3 },
  { key: "data", label: "Data & IT", count: 3 },
  { key: "people", label: "People", count: 3 },
];
const HERO_SEGMENT_COUNT = HERO_SECTION_DEFS.reduce((n, s) => n + s.count, 0);

/** Build the DialView view model from the current per-segment colours + completion fraction. */
function buildHeroVm(colours: string[], completion: number): DialVM {
  let i = 0;
  const sections: VSection[] = HERO_SECTION_DEFS.map((def) => {
    const segments: VSegment[] = Array.from({ length: def.count }, (_unused, j) => ({
      key: `${def.key}-${j}`,
      label: `${def.key}-${j}`,
      name: `${def.key}-${j}`,
      items: [],
      itemActive: false, // decorative sign-in dial — no items, so it reads as a simple segment
      colour: colours[i++] ?? RAG_STOPS[0],
      palette: [],
      overdue: false,
      done: 0,
      total: 1,
    }));
    return { key: def.key, label: def.label, segments, done: 0, total: segments.length };
  });
  return {
    detail: { id: "sign-in-sample", name: "Charity governance", centralImageUrl: null } as DialVM["detail"],
    simple: true,
    sections,
    dimensions: [],
    completion,
    done: 0,
    total: HERO_SEGMENT_COUNT,
    attention: 0,
    itemCount: HERO_SEGMENT_COUNT,
    sectionCount: sections.length,
    isGroup: false,
  };
}

// Animation tunables.
const HOLD_MS = 2500; // pause once every segment is green, before reverting
const REVERT_SPEED = 1.25; // per second — ~0.8s snap back to red
const FRAME_THROTTLE_MS = 40; // ~25fps React writes (the fade is slow, so this stays smooth and cheap)
const growSpeed = () => 0.1 + Math.random() * 0.1; // 0.10..0.20/s -> ~5-10s for a full red->green sweep
const growDelay = () => Math.random() * 2.5; // 0..2.5s stagger before a segment starts growing
const randomRag = () => [0, 0.5, 1][Math.floor(Math.random() * 3)]; // discrete red / amber / green

function prefersReducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** The animated hero gauge — a rAF loop cycles the segments red -> amber -> green and back. */
function HeroDial() {
  const reduced = prefersReducedMotion();

  // Seed once: a random sample of RAG so the dial opens on a believable mixed state.
  const progressRef = useRef<number[] | null>(null);
  if (progressRef.current === null) {
    progressRef.current = Array.from({ length: HERO_SEGMENT_COUNT }, randomRag);
  }
  const [{ colours, completion }, setFrame] = useState(() => {
    const p = progressRef.current!;
    return { colours: p.map(ragColour), completion: mean(p) };
  });

  useEffect(() => {
    if (reduced) {
      return; // honour reduced motion — leave the static initial sample in place
    }
    const progress = progressRef.current!;
    const speed = Array.from({ length: HERO_SEGMENT_COUNT }, growSpeed);
    const delay = Array.from({ length: HERO_SEGMENT_COUNT }, growDelay);
    let phase: "grow" | "hold" | "revert" = "grow";
    let holdElapsed = 0;
    let sinceWrite = 0;
    let last = performance.now();
    let raf = requestAnimationFrame(function tick(now) {
      const dt = Math.min(0.05, (now - last) / 1000); // clamp dt across tab refocus
      last = now;

      if (phase === "grow") {
        let allGreen = true;
        for (let i = 0; i < progress.length; i += 1) {
          if (delay[i] > 0) {
            delay[i] = Math.max(0, delay[i] - dt);
            allGreen = false;
            continue;
          }
          if (progress[i] < 1) {
            progress[i] = Math.min(1, progress[i] + speed[i] * dt);
          }
          if (progress[i] < 1) {
            allGreen = false;
          }
        }
        if (allGreen) {
          phase = "hold";
          holdElapsed = 0;
        }
      } else if (phase === "hold") {
        holdElapsed += dt;
        if (holdElapsed >= HOLD_MS / 1000) {
          phase = "revert";
        }
      } else {
        let allRed = true;
        for (let i = 0; i < progress.length; i += 1) {
          if (progress[i] > 0) {
            progress[i] = Math.max(0, progress[i] - REVERT_SPEED * dt);
            allRed = allRed && progress[i] === 0;
          }
        }
        if (allRed) {
          for (let i = 0; i < progress.length; i += 1) {
            speed[i] = growSpeed();
            delay[i] = growDelay();
          }
          phase = "grow";
        }
      }

      sinceWrite += dt * 1000;
      if (sinceWrite >= FRAME_THROTTLE_MS) {
        sinceWrite = 0;
        setFrame({ colours: progress.map(ragColour), completion: mean(progress) });
      }
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  return <DialView vm={buildHeroVm(colours, completion)} interactive={false} showLabels progress />;
}

// Render the SVG wordmark knocked to white on the dark brand panel (it themes via --navy).
const WHITE_LOGO = { "--navy": "#ffffff" } as CSSProperties;

const FEATURES = [
  "Live RAG status across every obligation",
  "Evidence and responses in one place",
  "A permanent, board-ready audit trail",
];

/** Small mail glyph (not in the shared icon set). */
function MailIcon({ size = 19 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x={2.5} y={4.5} width={19} height={15} rx={2.5} />
      <path d="M3 6l9 7 9-7" />
    </svg>
  );
}

// A pool of plausible (made-up) governance statuses the floating chips cycle through.
const CHIP_POOL: { colour: string; title: string; sub: string }[] = [
  { colour: SCALE.done, title: "On track", sub: "Client Onboarding" },
  { colour: SCALE.progress, title: "68% complete", sub: "Health & Safety" },
  { colour: SCALE.done, title: "Audit ready", sub: "Safeguarding" },
  { colour: SCALE.risk, title: "Needs review", sub: "Data Protection" },
  { colour: SCALE.done, title: "91% complete", sub: "Finance" },
  { colour: SCALE.progress, title: "In progress", sub: "Trustee Training" },
  { colour: SCALE.done, title: "Complete", sub: "Annual Return" },
  { colour: SCALE.progress, title: "73% complete", sub: "Risk Register" },
  { colour: SCALE.risk, title: "Action needed", sub: "Policy Review" },
  { colour: SCALE.done, title: "On track", sub: "Governance" },
];
const CHIP_INTERVAL_MS = 3500;

/** A floating status chip that periodically cross-fades to the next pool item. */
function AnimatedChip({
  style,
  startIndex,
  startDelayMs,
  animate,
}: {
  style: CSSProperties;
  startIndex: number;
  startDelayMs: number;
  animate: boolean;
}) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (!animate) {
      return;
    }
    let interval: ReturnType<typeof setInterval> | undefined;
    // Stagger the two chips so they never flip in unison.
    const startTimer = setTimeout(() => {
      interval = setInterval(() => setIndex((i) => (i + 1) % CHIP_POOL.length), CHIP_INTERVAL_MS);
    }, startDelayMs);
    return () => {
      clearTimeout(startTimer);
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [animate, startDelayMs]);

  const item = CHIP_POOL[index % CHIP_POOL.length];
  return (
    <div className="kd-chip" style={style}>
      <span style={{ width: 9, height: 9, borderRadius: 99, background: item.colour, flex: "none", transition: "background .4s ease" }} />
      {/* key forces a remount on change so the kd-fade entrance replays as a cross-fade */}
      <span key={index} style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, animation: animate ? "kd-fade .45s ease" : undefined }}>
        <span>{item.title}</span>
        <span className="sub">{item.sub}</span>
      </span>
    </div>
  );
}

function BrandPanel() {
  const animateChips = !prefersReducedMotion();
  return (
    <div className="kd-brand">
      <div className="kd-brand-logo" style={WHITE_LOGO}>
        <KdLogo height={36} />
      </div>

      <div className="kd-stage">
        <div className="kd-glow" />
        <AnimatedChip style={{ top: "12%", left: "2%", animationDelay: "0s" }} startIndex={0} startDelayMs={0} animate={animateChips} />
        <AnimatedChip style={{ bottom: "14%", right: "0%", animationDelay: "1.6s" }} startIndex={4} startDelayMs={1750} animate={animateChips} />
        <div className="kd-hero-dial">
          <HeroDial />
        </div>
      </div>

      <div className="kd-brand-foot">
        <h2>Making the complex, simple.</h2>
        <p>Key Dials turns sprawling assessment frameworks into a single dial your whole team can read at a glance.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 24 }}>
          {FEATURES.map((feature) => (
            <div key={feature} style={{ display: "flex", alignItems: "center", gap: 11, fontSize: 13.5, fontWeight: 500, color: "rgba(236,236,246,.86)" }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 99,
                  background: "rgba(62,176,52,.18)",
                  color: "#5fd154",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "none",
                }}
              >
                <Icon name="check" size={13} />
              </span>
              {feature}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Banner({ message }: { message: BannerMessage }) {
  if (!message) {
    return null;
  }
  const ok = message.type === "success";
  return (
    <div
      role={ok ? "status" : "alert"}
      style={{
        marginTop: 16,
        padding: "11px 13px",
        borderRadius: "var(--radius-sm)",
        fontSize: 13,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 9,
        color: ok ? "#1f7a18" : "#b3121a",
        background: ok ? "rgba(62,176,52,.1)" : "rgba(231,22,27,.08)",
        border: `1px solid ${ok ? "rgba(62,176,52,.28)" : "rgba(231,22,27,.26)"}`,
      }}
    >
      <Icon name={ok ? "check" : "flag"} size={15} />
      {message.text}
    </div>
  );
}

const emailInput: CSSProperties = {
  width: "100%",
  padding: "13px 14px 13px 42px",
  borderRadius: "var(--radius-sm)",
  border: "1.5px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 15,
  outline: "none",
};

function EmailStep({
  email,
  setEmail,
  loading,
  onSubmit,
}: {
  email: string;
  setEmail: (value: string) => void;
  loading: boolean;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit}>
      <label htmlFor="kd-email" style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 7 }}>
        Email address
      </label>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)", display: "flex" }}>
          <MailIcon />
        </span>
        <input
          id="kd-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@organisation.org"
          autoComplete="email"
          autoFocus
          className="kd-focusable"
          style={emailInput}
        />
      </div>
      <p style={{ margin: "9px 2px 0", fontSize: 12.5, color: "var(--text-faint)" }}>
        We&rsquo;ll email you a one-time 6-digit code — no password needed.
      </p>
      <button
        type="submit"
        disabled={loading}
        className="kd-btn-primary"
        style={{ marginTop: 20, width: "100%", height: 48, justifyContent: "center", fontSize: 15, opacity: loading ? 0.7 : 1 }}
      >
        {loading ? (
          "Sending…"
        ) : (
          <>
            Send code
            <Icon name="send" size={16} />
          </>
        )}
      </button>
    </form>
  );
}

function otpInput(filled: boolean): CSSProperties {
  return {
    width: 52,
    height: 60,
    flexShrink: 1,
    minWidth: 0,
    textAlign: "center",
    fontSize: 26,
    fontWeight: 800,
    fontFamily: "var(--font-display)",
    color: "var(--text)",
    borderRadius: "var(--radius-sm)",
    border: `1.5px solid ${filled ? "var(--accent-border)" : "var(--border)"}`,
    background: filled ? "var(--accent-soft)" : "var(--surface)",
    outline: "none",
    transition: "border-color .15s ease, background .15s ease",
  };
}

function otpLink(colour: string): CSSProperties {
  return { background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", fontSize: 13, fontWeight: 700, color: colour };
}

function OtpStep({
  otp,
  setOtp,
  loading,
  onVerify,
  onBack,
  onResend,
  cooldown,
}: {
  otp: string[];
  setOtp: (value: string[]) => void;
  loading: boolean;
  onVerify: (event: FormEvent) => void;
  onBack: () => void;
  onResend: () => void;
  cooldown: number;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const change = (i: number, value: string) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }
    const next = [...otp];
    next[i] = value;
    setOtp(next);
    if (value && i < OTP_LENGTH - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const keydown = (i: number, e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const paste = (e: ReactClipboardEvent<HTMLInputElement>) => {
    const pasted = (e.clipboardData.getData("text") || "").trim();
    if (new RegExp(`^\\d{${OTP_LENGTH}}$`).test(pasted)) {
      e.preventDefault();
      setOtp(pasted.split(""));
      refs.current[OTP_LENGTH - 1]?.focus();
    }
  };

  const full = otp.join("").length === OTP_LENGTH;

  return (
    <form onSubmit={onVerify}>
      <div style={{ display: "flex", gap: 9, justifyContent: "space-between" }} onPaste={paste}>
        {otp.map((digit, i) => (
          <input
            // Fixed-length, positional inputs — the index is a stable key here.
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => change(i, e.target.value)}
            onKeyDown={(e) => keydown(i, e)}
            aria-label={`Digit ${i + 1}`}
            className="kd-focusable"
            style={otpInput(!!digit)}
          />
        ))}
      </div>
      <button
        type="submit"
        disabled={loading || !full}
        className="kd-btn-primary"
        style={{
          marginTop: 22,
          width: "100%",
          height: 48,
          justifyContent: "center",
          fontSize: 15,
          opacity: loading || !full ? 0.55 : 1,
          cursor: loading || !full ? "not-allowed" : "pointer",
        }}
      >
        {loading ? (
          "Verifying…"
        ) : (
          <>
            Verify &amp; sign in
            <Icon name="check" size={17} />
          </>
        )}
      </button>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 16, fontSize: 13, color: "var(--text-muted)" }}>
        {cooldown > 0 ? (
          <span>Resend code in {cooldown}s</span>
        ) : (
          <button type="button" onClick={onResend} style={otpLink("var(--accent-text)")}>
            Resend code
          </button>
        )}
        <span style={{ color: "var(--border-strong)" }}>·</span>
        <button type="button" onClick={onBack} style={otpLink("var(--text-muted)")}>
          Change email
        </button>
      </div>
    </form>
  );
}

export function Login() {
  const { requestCode, verifyCode, error } = useAuth();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<BannerMessage>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // Surface verify failures (set on the auth context) as an inline error banner.
  useEffect(() => {
    if (error) {
      setMessage({ type: "error", text: error });
    }
  }, [error]);

  // Request a code; resolves regardless of whether the email exists (the API reveals nothing).
  async function sendCode() {
    setLoading(true);
    await requestCode(email.trim());
    setLoading(false);
    setCooldown(RESEND_SECONDS);
  }

  async function onEmailSubmit(event: FormEvent) {
    event.preventDefault();
    if (loading) {
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setMessage({ type: "error", text: "Enter a valid email address." });
      return;
    }
    setMessage(null);
    await sendCode();
    setOtp(Array(OTP_LENGTH).fill(""));
    setStep("otp");
    setMessage({ type: "success", text: "Code sent — check your inbox." });
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (loading) {
      return;
    }
    if (otp.join("").length !== OTP_LENGTH) {
      setMessage({ type: "error", text: "Enter all 6 digits." });
      return;
    }
    setMessage(null);
    setLoading(true);
    // On success the auth context sets `me`, which swaps this whole screen for the app.
    const ok = await verifyCode(email.trim(), otp.join(""));
    setLoading(false);
    if (!ok) {
      setOtp(Array(OTP_LENGTH).fill(""));
      refocusFirstOtp();
    }
  }

  async function onResend() {
    setMessage(null);
    await sendCode();
    setMessage({ type: "success", text: "A fresh code is on its way." });
  }

  function onBack() {
    setStep("email");
    setOtp(Array(OTP_LENGTH).fill(""));
    setMessage(null);
  }

  const heading = step === "email" ? "Sign in to Key Dials" : "Check your email";

  return (
    <div className="kd-auth-wrap">
      <BrandPanel />
      <div className="kd-form-panel">
        <div className="kd-form">
          <div className="kd-form-logo">
            <KdLogo height={38} />
          </div>

          <div style={{ marginBottom: 26 }}>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 27, fontWeight: 800, letterSpacing: "-.01em", margin: 0, color: "var(--text)" }}>
              {heading}
            </h1>
            {step === "otp" ? (
              <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 14.5, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                We sent a 6-digit code to <strong style={{ color: "var(--text)" }}>{email || "your email"}</strong>.
              </p>
            ) : (
              <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 14.5 }}>Welcome back. Enter your email to continue.</p>
            )}
          </div>

          {step === "email" ? (
            <EmailStep email={email} setEmail={setEmail} loading={loading} onSubmit={onEmailSubmit} />
          ) : (
            <OtpStep otp={otp} setOtp={setOtp} loading={loading} onVerify={onVerify} onBack={onBack} onResend={onResend} cooldown={cooldown} />
          )}

          <Banner message={message} />

          {step === "email" && (
            <p style={{ marginTop: 26, paddingTop: 20, borderTop: "1px solid var(--border)", fontSize: 13, color: "var(--text-faint)", textAlign: "center" }}>
              No account yet? Ask your organisation admin for an invite.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function refocusFirstOtp() {
  // The OTP inputs are inside OtpStep; after a failed verify we clear them and nudge focus back to
  // the first box on the next frame (once React has re-rendered the empties).
  requestAnimationFrame(() => {
    const first = document.querySelector<HTMLInputElement>('input[aria-label="Digit 1"]');
    first?.focus();
  });
}
